import React, { useState } from 'react';
import { Row, Col, Card, Tree, Select, Radio, Button, Tooltip, Divider, Tabs } from 'antd';
import { InfoCircleOutlined, DownloadOutlined, FilterOutlined, SyncOutlined } from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import './index.css';

const { TreeNode } = Tree;
const { Option } = Select;
const { TabPane } = Tabs;

// 模拟数据
const mockIndicators = {
  '政务服务': {
    '企业开办': ['开办时长', '办理成本', '满意度'],
    '工程建设': ['审批用时', '审批环节', '透明度'],
    '纳税服务': ['纳税时间', '纳税成本', '申报便捷度']
  },
  '市场监管': {
    '市场准入': ['准入门槛', '审批效率', '公平性'],
    '监管执法': ['监管频次', '执法规范性', '投诉处理'],
    '知识产权': ['保护力度', '侵权处理', '专利授权']
  },
  '金融服务': {
    '融资环境': ['融资难度', '融资成本', '融资时间'],
    '金融创新': ['产品多样性', '服务便捷度', '创新支持']
  }
};

const mockRegions = [
  { value: 'all', label: '全省' },
  { value: 'city1', label: '城东区' },
  { value: 'city2', label: '西湖区' },
  { value: 'city3', label: '河北区' },
  { value: 'city4', label: '南山区' },
  { value: 'city5', label: '江北区' },
];

const mockIndustries = [
  { value: 'all', label: '全行业' },
  { value: 'industry1', label: '制造业' },
  { value: 'industry2', label: '信息技术' },
  { value: 'industry3', label: '金融业' },
  { value: 'industry4', label: '建筑业' },
  { value: 'industry5', label: '批发零售业' },
];

const mockTimeRanges = [
  { value: '1m', label: '近1个月' },
  { value: '3m', label: '近3个月' },
  { value: '6m', label: '近6个月' },
  { value: '1y', label: '近1年' },
  { value: 'custom', label: '自定义' },
];

const IndicatorAnalysis = () => {
  const [selectedIndicators, setSelectedIndicators] = useState(['政务服务-企业开办-开办时长']);
  const [region, setRegion] = useState('all');
  const [industry, setIndustry] = useState('all');
  const [timeRange, setTimeRange] = useState('6m');
  const [chartType, setChartType] = useState('trend');
  
  // 构建指标树
  const renderTreeNodes = (data) => {
    return Object.entries(data).map(([key, value]) => {
      if (typeof value === 'object') {
        return (
          <TreeNode title={key} key={key}>
            {renderTreeNodes(value)}
          </TreeNode>
        );
      }
      if (Array.isArray(value)) {
        return (
          <TreeNode title={key} key={key}>
            {value.map(item => (
              <TreeNode title={item} key={`${key}-${item}`} isLeaf />
            ))}
          </TreeNode>
        );
      }
      return <TreeNode title={value} key={value} isLeaf />;
    });
  };
  
  // 处理树选择事件
  const handleTreeSelect = (selectedKeys) => {
    // 这里仅处理叶子节点
    const leafNodes = selectedKeys.filter(key => key.split('-').length === 3);
    setSelectedIndicators(leafNodes);
  };
  
  // 趋势图的配置
  const getTrendChartOption = () => {
    return {
      title: {
        text: '指标趋势分析',
        left: 'center'
      },
      tooltip: {
        trigger: 'axis'
      },
      legend: {
        data: selectedIndicators.map(ind => ind.split('-')[2]),
        bottom: 10
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '15%',
        top: '15%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: ['1月', '2月', '3月', '4月', '5月', '6月']
      },
      yAxis: {
        type: 'value',
        name: '指标值',
        nameLocation: 'end'
      },
      series: selectedIndicators.map((ind, index) => ({
        name: ind.split('-')[2],
        type: 'line',
        data: Array(6).fill(0).map(() => Math.floor(Math.random() * 50 + 50)),
        smooth: true,
        lineStyle: {
          width: 3
        },
        areaStyle: {
          opacity: 0.1
        }
      }))
    };
  };
  
  // 对比图的配置
  const getComparisonChartOption = () => {
    const categories = ['城东区', '西湖区', '河北区', '南山区', '江北区'];
    return {
      title: {
        text: '区域对比分析',
        left: 'center'
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        }
      },
      legend: {
        data: selectedIndicators.map(ind => ind.split('-')[2]),
        bottom: 10
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '15%',
        top: '15%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: categories
      },
      yAxis: {
        type: 'value',
        name: '指标值',
      },
      series: selectedIndicators.map((ind, index) => ({
        name: ind.split('-')[2],
        type: 'bar',
        data: categories.map(() => Math.floor(Math.random() * 50 + 50)),
        emphasis: {
          focus: 'series'
        },
        barMaxWidth: 50
      }))
    };
  };
  
  // 散点图的配置
  const getScatterChartOption = () => {
    // 随机生成散点数据
    const generateScatterData = () => {
      return Array(30).fill(0).map(() => [
        Math.floor(Math.random() * 80 + 20),
        Math.floor(Math.random() * 80 + 20),
        Math.floor(Math.random() * 30 + 10) // 气泡大小
      ]);
    };
    
    return {
      title: {
        text: '多维对比分析',
        left: 'center'
      },
      tooltip: {
        trigger: 'item',
        formatter: function(params) {
          return `${params.seriesName}<br/>
                  X轴值: ${params.value[0]}<br/>
                  Y轴值: ${params.value[1]}<br/>
                  规模: ${params.value[2]}`;
        }
      },
      legend: {
        data: selectedIndicators.map(ind => ind.split('-')[2]),
        bottom: 10
      },
      xAxis: {
        name: selectedIndicators[0]?.split('-')[2] || '指标X',
        nameLocation: 'end',
        type: 'value',
        scale: true
      },
      yAxis: {
        name: selectedIndicators[1]?.split('-')[2] || '指标Y',
        nameLocation: 'end',
        type: 'value',
        scale: true
      },
      series: selectedIndicators.slice(0, 2).map((ind, index) => ({
        name: ind.split('-')[2],
        type: 'scatter',
        symbolSize: function(data) {
          return data[2];
        },
        data: generateScatterData()
      }))
    };
  };
  
  // 热力图的配置
  const getHeatmapChartOption = () => {
    const hours = ['12a', '1a', '2a', '3a', '4a', '5a', '6a',
      '7a', '8a', '9a', '10a', '11a',
      '12p', '1p', '2p', '3p', '4p', '5p',
      '6p', '7p', '8p', '9p', '10p', '11p'];
    const days = ['周一', '周二', '周三', '周四', '周五', '周六', '周日'];

    // 随机生成热力图数据
    const generateHeatmapData = () => {
      return days.map((day, dayIndex) => {
        return hours.map((hour, hourIndex) => {
          return [hourIndex, dayIndex, Math.floor(Math.random() * 10)];
        });
      }).flat();
    };
    
    return {
      title: {
        text: '热力图分析',
        left: 'center'
      },
      tooltip: {
        position: 'top'
      },
      grid: {
        height: '50%',
        top: '15%'
      },
      xAxis: {
        type: 'category',
        data: hours,
        splitArea: {
          show: true
        }
      },
      yAxis: {
        type: 'category',
        data: days,
        splitArea: {
          show: true
        }
      },
      visualMap: {
        min: 0,
        max: 10,
        calculable: true,
        orient: 'horizontal',
        left: 'center',
        bottom: '0%'
      },
      series: [{
        name: selectedIndicators[0]?.split('-')[2] || '指标热力分布',
        type: 'heatmap',
        data: generateHeatmapData(),
        label: {
          show: false
        },
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowColor: 'rgba(0, 0, 0, 0.5)'
          }
        }
      }]
    };
  };
  
  // 获取当前选中的图表配置
  const getChartOption = () => {
    switch (chartType) {
      case 'trend':
        return getTrendChartOption();
      case 'comparison':
        return getComparisonChartOption();
      case 'scatter':
        return getScatterChartOption();
      case 'heatmap':
        return getHeatmapChartOption();
      default:
        return getTrendChartOption();
    }
  };
  
  // 指标说明内容
  const getIndicatorDescription = () => {
    if (selectedIndicators.length === 0) return '请选择指标查看说明';
    
    const [category, subcategory, indicator] = selectedIndicators[0].split('-');
    
    const descriptions = {
      '企业开办-开办时长': '企业从申请到获得营业执照的平均时间，单位为工作日。计算方法：所有企业开办时间之和/企业数量。',
      '企业开办-办理成本': '企业开办过程中的行政规费和服务性收费总和，单位为元。',
      '企业开办-满意度': '企业对开办过程的满意程度，基于问卷调查，满分100分。',
      '工程建设-审批用时': '工程建设项目从申请到获得施工许可证的平均时间，单位为工作日。',
      '纳税服务-纳税时间': '企业完成全部纳税申报和缴纳的平均工时，单位为小时/年。'
    };
    
    return descriptions[`${subcategory}-${indicator}`] || 
      `${category}-${subcategory}-${indicator}：该指标反映了${subcategory}的${indicator}情况，是衡量营商环境的重要参考。`;
  };
  
  return (
    <div className="indicator-container">
      <div className="indicator-header">
        <h1>指标监测分析</h1>
        <div className="indicator-actions">
          <Button icon={<DownloadOutlined />}>导出分析</Button>
          <Button icon={<SyncOutlined />}>刷新数据</Button>
        </div>
      </div>
      
      <Row gutter={[16, 16]}>
        {/* 左侧指标树 */}
        <Col xs={24} lg={6}>
          <Card title="指标体系" className="indicator-card">
            <div className="indicator-filter">
              <Select 
                placeholder="选择区域" 
                style={{ width: '100%', marginBottom: 12 }} 
                value={region} 
                onChange={setRegion}
              >
                {mockRegions.map(item => (
                  <Option key={item.value} value={item.value}>{item.label}</Option>
                ))}
              </Select>
              
              <Select 
                placeholder="选择行业" 
                style={{ width: '100%', marginBottom: 12 }} 
                value={industry} 
                onChange={setIndustry}
              >
                {mockIndustries.map(item => (
                  <Option key={item.value} value={item.value}>{item.label}</Option>
                ))}
              </Select>
              
              <Select 
                placeholder="选择时间" 
                style={{ width: '100%' }} 
                value={timeRange} 
                onChange={setTimeRange}
              >
                {mockTimeRanges.map(item => (
                  <Option key={item.value} value={item.value}>{item.label}</Option>
                ))}
              </Select>
            </div>
            
            <Divider />
            
            <div className="indicator-tree-container">
              <Tree
                checkable
                onCheck={handleTreeSelect}
                defaultExpandAll
              >
                {renderTreeNodes(mockIndicators)}
              </Tree>
            </div>
          </Card>
        </Col>
        
        {/* 右侧数据看板 */}
        <Col xs={24} lg={18}>
          <Card 
            title={
              <div className="chart-card-title">
                <span>数据分析</span>
                <div className="chart-type-selector">
                  <Radio.Group value={chartType} onChange={e => setChartType(e.target.value)}>
                    <Radio.Button value="trend">趋势分析</Radio.Button>
                    <Radio.Button value="comparison">对比分析</Radio.Button>
                    <Radio.Button value="scatter">散点分析</Radio.Button>
                    <Radio.Button value="heatmap">热力分析</Radio.Button>
                  </Radio.Group>
                </div>
              </div>
            } 
            className="indicator-card"
          >
            <div className="chart-container">
              {selectedIndicators.length > 0 ? (
                <ReactECharts
                  option={getChartOption()}
                  style={{ height: '500px' }}
                />
              ) : (
                <div className="empty-chart">
                  <InfoCircleOutlined />
                  <p>请从左侧选择指标进行分析</p>
                </div>
              )}
            </div>
          </Card>
          
          {selectedIndicators.length > 0 && (
            <Card title="指标解释" className="indicator-card indicator-desc-card">
              <Tabs defaultActiveKey="desc">
                <TabPane tab="指标说明" key="desc">
                  <div className="indicator-desc">
                    <p>{getIndicatorDescription()}</p>
                  </div>
                </TabPane>
                <TabPane tab="计算方法" key="calc">
                  <div className="indicator-desc">
                    <p>本指标采用加权平均算法，根据各区域企业数量比例进行加权。计算公式：∑(区域指标值×权重)/∑权重</p>
                  </div>
                </TabPane>
                <TabPane tab="数据来源" key="source">
                  <div className="indicator-desc">
                    <p>数据来源于政务服务平台业务系统、企业调查问卷及第三方评估机构</p>
                  </div>
                </TabPane>
              </Tabs>
            </Card>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default IndicatorAnalysis; 